import { useCallback, useEffect, useState } from "react";

import { DateTimeDisplay } from "../../components/date-time-display";
import { ErrorNotice, LoadingView } from "../../components/feedback";
import { requestAdminApi } from "../../lib/api-client";
import type { AnnouncementLocale, PushCampaign, PushPublication, PushRetry } from "../../types";
import { AnnouncementLocaleTabs } from "./announcement-locale-tabs";
import { formatCampaignStatus } from "./push-display";

type PushDetailScreenProps = {
  campaignId: string;
  onBack: () => void;
  onEditDraft: (campaignId: string) => void;
};

/**
 * お知らせ配信の詳細画面
 *
 * @param props.campaignId - 表示する配信ID
 * @param props.onBack - 配信履歴へ戻る処理
 * @param props.onEditDraft - 作成・編集画面を開く処理
 * @returns お知らせ配信の詳細画面
 */
export const PushDetailScreen = ({ campaignId, onBack, onEditDraft }: PushDetailScreenProps) => {
  // 配信詳細を保持する
  const [campaign, setCampaign] = useState<PushCampaign | null>(null);

  // 詳細読み込み中かを保持する
  const [isLoading, setIsLoading] = useState(true);

  // 操作中の状態を保持する
  const [activeAction, setActiveAction] = useState<"copy" | "retry" | "resume" | null>(null);

  // 詳細取得・操作のエラーを保持する
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 完了通知を保持する
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // 表示中のお知らせ本文の言語を保持する
  const [activeLocale, setActiveLocale] = useState<AnnouncementLocale>("en");

  // 配信詳細を読み込む
  const loadCampaign = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const result = await requestAdminApi<PushCampaign>(
        `/v1/admin/push-campaigns/${encodeURIComponent(campaignId)}`,
      );
      setCampaign(result);
    } catch (error: unknown) {
      console.error("Failed to load push campaign detail", error);
      setErrorMessage("配信内容を読み込めませんでした。");
    } finally {
      setIsLoading(false);
    }
  }, [campaignId]);

  // 初回表示時に配信詳細を読み込む
  useEffect(() => {
    void loadCampaign();
  }, [loadCampaign]);

  // 詳細画面から配信操作を実行する
  const handleAction = async (action: "copy" | "retry" | "resume") => {
    setActiveAction(action);
    setErrorMessage(null);

    try {
      if (action === "copy") {
        const copiedCampaign = await requestAdminApi<PushCampaign>(
          `/v1/admin/push-campaigns/${encodeURIComponent(campaignId)}/copies`,
          { method: "POST" },
        );
        onEditDraft(copiedCampaign.campaign_id);
        return;
      }

      if (action === "retry") {
        await requestAdminApi<PushRetry>(
          `/v1/admin/push-campaigns/${encodeURIComponent(campaignId)}/retry`,
          { method: "POST" },
        );
        setSuccessMessage("現在有効な対象端末への再配信を受け付けました。");
      } else {
        await requestAdminApi<PushPublication>(
          `/v1/admin/push-campaigns/${encodeURIComponent(campaignId)}/publish`,
          {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
          },
        );
        setSuccessMessage("お知らせの公開処理を再開しました。");
      }

      await loadCampaign();
    } catch (error: unknown) {
      console.error("Failed to operate push campaign", error);
      setErrorMessage("配信操作を完了できませんでした。");
    } finally {
      setActiveAction(null);
    }
  };

  if (isLoading && campaign === null) {
    return <LoadingView label="配信内容を読み込んでいます" />;
  }

  if (campaign === null) {
    return (
      <div className="page-stack">
        {errorMessage ? (
          <ErrorNotice message={errorMessage} onRetry={() => void loadCampaign()} />
        ) : null}
        <button className="button button--secondary align-start" type="button" onClick={onBack}>
          配信履歴に戻る
        </button>
      </div>
    );
  }

  const hasRetryableFailures =
    campaign.failed_count > 0 &&
    (campaign.status === "partial_failed" || campaign.status === "failed");
  const canResumePublication = campaign.status !== "draft" && !campaign.publication_ready;
  const isJapaneseLocale = activeLocale === "ja";
  const activeLocalizedContent = campaign.localizations[activeLocale];

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">配信詳細</p>
          <h1>
            {activeLocalizedContent.title ||
              (isJapaneseLocale ? "日本語タイトル未入力" : "English title is not set")}
          </h1>
          <p>
            <DateTimeDisplay value={campaign.created_at} /> · 配信ID {campaign.campaign_id}
          </p>
        </div>
        <button className="button button--secondary" type="button" onClick={onBack}>
          配信履歴に戻る
        </button>
      </section>

      {errorMessage ? <ErrorNotice message={errorMessage} /> : null}
      {successMessage ? (
        <div className="notice notice--success" role="status">
          <span aria-hidden="true">✓</span>
          <p>{successMessage}</p>
        </div>
      ) : null}

      <div className="detail-layout">
        <div className="detail-main">
          <section className="panel campaign-detail-panel" aria-labelledby="campaign-content-title">
            <div className="panel__header">
              <div>
                <p className="eyebrow">配信内容</p>
                <h2 id="campaign-content-title">お知らせ本文</h2>
              </div>
              <span className={`badge badge--${campaign.status}`}>
                {formatCampaignStatus(campaign.status)}
              </span>
            </div>
            <div className="campaign-detail-content">
              <AnnouncementLocaleTabs
                activeLocale={activeLocale}
                englishComplete={
                  campaign.localizations.en.title.trim().length > 0 &&
                  campaign.localizations.en.body.trim().length > 0
                }
                japaneseComplete={
                  campaign.localizations.ja.title.trim().length > 0 &&
                  campaign.localizations.ja.body.trim().length > 0
                }
                onChange={setActiveLocale}
              />
              <section
                className="localized-content campaign-detail-content__panel"
                role="tabpanel"
                aria-label={
                  isJapaneseLocale ? "日本語のお知らせ内容" : "English announcement content"
                }
              >
                <div className="localized-content__header">
                  <h3>{isJapaneseLocale ? "日本語" : "English"}</h3>
                </div>
                <strong>
                  {activeLocalizedContent.title ||
                    (isJapaneseLocale ? "日本語タイトル未入力" : "English title is not set")}
                </strong>
                <p className="campaign-detail-body">
                  {activeLocalizedContent.body ||
                    (isJapaneseLocale ? "日本語本文未入力" : "English body is not set")}
                </p>
              </section>
            </div>
          </section>
        </div>

        <aside className="detail-aside">
          <section
            className="panel campaign-detail-summary"
            aria-labelledby="campaign-summary-title"
          >
            <div className="panel__header">
              <div>
                <p className="eyebrow">配信結果</p>
                <h2 id="campaign-summary-title">集計</h2>
              </div>
            </div>
            <dl className="campaign-detail-stats">
              <div>
                <dt>配信対象ユーザー</dt>
                <dd>{campaign.target_user_count.toLocaleString("ja-JP")}人</dd>
              </div>
              <div>
                <dt>対象端末</dt>
                <dd>{campaign.target_installation_count.toLocaleString("ja-JP")}台</dd>
              </div>
              <div>
                <dt>送信成功</dt>
                <dd>{campaign.sent_count.toLocaleString("ja-JP")}件</dd>
              </div>
              <div>
                <dt>送信失敗</dt>
                <dd>{campaign.failed_count.toLocaleString("ja-JP")}件</dd>
              </div>
              {campaign.invalid_token_count > 0 ? (
                <div>
                  <dt>無効な端末</dt>
                  <dd>{campaign.invalid_token_count.toLocaleString("ja-JP")}件</dd>
                </div>
              ) : null}
              <div>
                <dt>公開日時</dt>
                <dd>
                  {campaign.published_at ? (
                    <DateTimeDisplay value={campaign.published_at} />
                  ) : (
                    "未公開"
                  )}
                </dd>
              </div>
            </dl>
          </section>

          <section
            className="panel campaign-detail-actions"
            aria-labelledby="campaign-actions-title"
          >
            <div className="panel__header">
              <div>
                <p className="eyebrow">配信操作</p>
                <h2 id="campaign-actions-title">この配信を操作</h2>
              </div>
            </div>
            <div className="campaign-detail-actions__list">
              {campaign.status === "draft" ? (
                <button
                  className="button button--primary button--full"
                  type="button"
                  onClick={() => onEditDraft(campaign.campaign_id)}
                >
                  下書きを編集
                </button>
              ) : (
                <button
                  className="button button--secondary button--full"
                  type="button"
                  disabled={activeAction !== null}
                  onClick={() => void handleAction("copy")}
                >
                  {activeAction === "copy" ? "訂正版を作成中…" : "訂正版を作成"}
                </button>
              )}
              {hasRetryableFailures ? (
                <button
                  className="button button--secondary button--full"
                  type="button"
                  disabled={activeAction !== null}
                  onClick={() => void handleAction("retry")}
                >
                  {activeAction === "retry" ? "再配信中…" : "対象端末へ再配信"}
                </button>
              ) : null}
              {canResumePublication ? (
                <button
                  className="button button--secondary button--full"
                  type="button"
                  disabled={activeAction !== null}
                  onClick={() => void handleAction("resume")}
                >
                  {activeAction === "resume" ? "公開再開中…" : "公開処理を再開"}
                </button>
              ) : null}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};
